import { GraphNode } from './graph';

export function fixResolution(
  canvas: HTMLCanvasElement,
  dpi: number
): CanvasRenderingContext2D {
  const sizes = canvas.getBoundingClientRect();
  canvas.width = sizes.width * dpi;
  canvas.height = sizes.height * dpi;

  const ctx = canvas.getContext('2d');
  ctx.scale(dpi, dpi);
  return ctx;
}

export function drawGraph(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  nodes: GraphNode[],
  {
    size,
    colors,
    fontColors,
  }: { size?: number; colors?: string[]; fontColors?: string[] }
): void {
  const lineMap = {};

  nodes.forEach((n, i) => {
    let color = '';
    if (colors && i < colors.length - 1) {
      color = colors[i];
    }

    let fontColor = '';
    if (fontColors && i < fontColors.length - 1) {
      fontColor = fontColors[i];
    }

    const lines = [];
    for (const neighbour of n.neighbours) {
      const key1 = `${n.value}|${neighbour.value}`;
      const key2 = `${neighbour.value}|${n.value}`;
      if (key1 in lineMap || key2 in lineMap) continue;
      lineMap[key1] = true;
      lines.push(neighbour.value);
    }

    drawGraphNode(canvas, ctx, n, { size, color, fontColor, lines });
  });
}

export function drawGraphNode(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  node: GraphNode,
  {
    size,
    color,
    fontColor,
    lineColor,
    lines,
  }: {
    size?: number;
    color?: string;
    fontColor?: string;
    lineColor?: string;
    lines?: string[];
  }
): void {
  if (!ctx) return;

  const dimension = canvas.getBoundingClientRect();

  size = size || 20;
  color = color || '#fff';
  fontColor = fontColor || '#000';
  lineColor = lineColor || '#fff';

  if (node.neighbours) {
    let neighbours = node.neighbours;

    if (lines) {
      neighbours = neighbours.filter(n => lines.indexOf(n.value) > -1);
    }

    for (const neighbour of neighbours) {
      ctx.strokeStyle = lineColor;

      let startX = node.coords[0] * dimension.width;
      let startY = node.coords[1] * dimension.height;
      let endX = neighbour.coords[0] * dimension.width;
      let endY = neighbour.coords[1] * dimension.height;

      if (startX === endX) {
        endY += size;
      } else if (startY === endY) {
        endX += size;
      } else {
        if (endX > startX) {
          startX += size;
        } else {
          endX += size;
        }

        if (endY > startY) {
          startY += size / 2;
        } else {
          endY += size / 2;
        }
      }

      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
  }

  ctx.beginPath();
  ctx.fillStyle = color;
  ctx.arc(
    node.coords[0] * dimension.width,
    node.coords[1] * dimension.height,
    size,
    0,
    Math.PI * 2,
    false
  );
  ctx.fill();

  ctx.font = `${size - 5}px sans-serf`;
  ctx.fillStyle = fontColor;
  ctx.textAlign = 'center';
  ctx.fillText(
    node.value,
    node.coords[0] * dimension.width,
    node.coords[1] * dimension.height + 5
  );
}

export function drawArr(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  arr: number[],
  {
    size,
    fillColors,
    strokeColors,
    fontColors,
  }: {
    size?: number;
    fillColors?: string[];
    strokeColors?: string[];
    fontColors?: string[];
  }
): void {
  const dimension = canvas.getBoundingClientRect();

  size = (size || 0.08) * dimension.width;
  fillColors = fillColors || arr.map(() => '#fff');
  strokeColors = strokeColors || arr.map(() => '#5d5d5d');
  fontColors = fontColors || arr.map(() => '#000');

  const startX = dimension.width * 0.15;
  const startY = dimension.height * 0.4;

  for (let i = 0, len = arr.length; i < len; ++i) {
    const x = startX + size * i;

    ctx.beginPath();
    ctx.fillStyle = fillColors[i];
    ctx.rect(x, startY, size, size);
    ctx.fill();

    ctx.strokeStyle = strokeColors[i];
    ctx.strokeRect(x, startY, size, size);

    ctx.font = `${size * 0.5}px sans-serf`;
    ctx.fillStyle = fontColors[i];
    ctx.textAlign = 'center';
    ctx.fillText(arr[i].toString(), x + size / 2, startY + size / 1.5);
  }
}
